/**
 * GovernanceHotspotsCard - Executive dashboard card showing governance impact hotspots
 * Shows top work nodes with most linked governance artifacts
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle, AlertCircle, GitBranch, Link2, RefreshCw,
  Filter, ChevronRight, TrendingUp, Eye, Calendar
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useProductMode } from '../context/ProductModeContext';
import { trackEvent, TELEMETRY_EVENTS } from '../utils/telemetry';

const ARTIFACT_ICONS = {
  risk: AlertTriangle,
  issue: AlertCircle,
  decision: GitBranch,
  dependency: Link2
};

const ARTIFACT_COLORS = {
  risk: 'text-red-600 bg-red-50 border-red-200',
  issue: 'text-orange-600 bg-orange-50 border-orange-200',
  decision: 'text-blue-600 bg-blue-50 border-blue-200',
  dependency: 'text-purple-600 bg-purple-50 border-purple-200'
};

const GovernanceHotspotsCard = ({ 
  scopeId, 
  scopeType = 'portfolio', 
  onHotspotClick,
  onViewAll 
}) => {
  const { user } = useAuth();
  const { getFeature } = useProductMode();
  
  const tenantId = user?.tenant?.id || user?.tenant_id;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [window, setWindow] = useState(30);
  const [scope, setScope] = useState(scopeType);
  
  // Check feature flag
  const featureEnabled = useMemo(() => {
    if (typeof getFeature !== 'function') return false;
    return getFeature('governance.relationships', false);
  }, [getFeature]);
  
  useEffect(() => {
    if (featureEnabled && scopeId && tenantId) {
      loadRollup();
    } else {
      setLoading(false);
    }
  }, [scopeId, tenantId, window, scope, featureEnabled]);
  
  const loadRollup = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.getGovernanceRollup(scope, scopeId, tenantId, window);
      setData(response.data);
      
      // Track telemetry
      trackEvent(TELEMETRY_EVENTS.GOV_ROLLUP_VIEW_OPENED, {
        scope,
        scopeId,
        window
      });
    } catch (err) {
      console.error('Failed to load governance rollup:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const handleHotspotClick = (hotspot) => {
    trackEvent(TELEMETRY_EVENTS.GOV_HOTSPOT_CLICKED, {
      workId: hotspot.workId,
      type: hotspot.type
    });
    onHotspotClick?.(hotspot);
  };
  
  if (!featureEnabled) {
    return null;
  }
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="governance-hotspots-card">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-gray-900">Governance Hotspots</h3>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Window toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setWindow(30)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  window === 30 ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                30d
              </button>
              <button
                onClick={() => setWindow(90)}
                className={`px-2 py-1 text-xs font-medium rounded-md transition-colors ${
                  window === 90 ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                90d
              </button>
            </div>
            
            {/* Refresh */}
            <button
              onClick={loadRollup}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>
      
      {/* Totals */}
      {!loading && data?.totals && (
        <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
          <div className="text-center">
            <div className="text-lg font-bold text-red-600">{data.totals.risks}</div>
            <div className="text-[10px] text-gray-500 uppercase">Risks</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-orange-600">{data.totals.issues}</div>
            <div className="text-[10px] text-gray-500 uppercase">Issues</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{data.totals.decisions}</div>
            <div className="text-[10px] text-gray-500 uppercase">Decisions</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">{data.totals.deps}</div>
            <div className="text-[10px] text-gray-500 uppercase">Deps</div>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8 text-sm text-red-600">
            {error}
          </div>
        ) : !data?.hotspots?.length ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-sm text-gray-600 font-medium">All Clear</p>
            <p className="text-xs text-gray-500 mt-1">No governance hotspots detected</p>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Hotspots table */}
            <div className="text-xs text-gray-500 uppercase mb-2">Top 10 by Link Count</div>
            {data.hotspots.map((hotspot, idx) => {
              const Icon = ARTIFACT_ICONS[hotspot.type] || Link2;
              const colors = ARTIFACT_COLORS[hotspot.type] || ARTIFACT_COLORS.dependency;
              
              return (
                <div
                  key={`${hotspot.workId}-${idx}`}
                  onClick={() => handleHotspotClick(hotspot)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer group"
                  data-testid={`hotspot-${hotspot.workId}`}
                >
                  <span className="text-xs text-gray-400 w-4">{idx + 1}</span>
                  <div className={`p-1 rounded border ${colors}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800 truncate">{hotspot.workTitle}</div>
                    {hotspot.workPath && (
                      <div className="text-[10px] text-gray-400 truncate">{hotspot.workPath}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-900">{hotspot.count}</div>
                    <div className="text-[10px] text-gray-400">{hotspot.type}s</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500" />
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Footer - View All */}
      {data?.hotspots?.length > 0 && onViewAll && (
        <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl">
          <button
            onClick={onViewAll}
            className="w-full flex items-center justify-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
          >
            <Eye className="w-4 h-4" />
            View All Governance Links
          </button>
        </div>
      )}
    </div>
  );
};

export default GovernanceHotspotsCard;
